import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { sanitizeHtml, normalizeSafeUrl } from '@/lib/sanitizeHtml';
import {
  Bold, Italic, Underline, Strikethrough, List, ListOrdered,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Link as LinkIcon,
  Image as ImageIcon, Code, Quote, Minus, Subscript,
  Superscript, Palette, Highlighter, Indent, Outdent, Code2, Eye,
  Edit3, Sparkles, Table2, Upload, X, FileImage, Pencil, Trash2,
  ArrowUp, ArrowDown, Maximize2, Plus
} from 'lucide-react';
import { Dialog as RadixDialog, DialogContent as RadixDialogContent } from '@/components/ui/dialog';

// Lightweight Modal wrapper compatible with legacy isOpen/onClose/title API
function Modal({ isOpen, onClose, title, children, maxWidth = 'md', zIndexClass = 'z-50' }) {
  if (!isOpen) return null;
  return (
    <div
      className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/60 p-4`}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className={`bg-white rounded-xl shadow-2xl w-full max-w-${maxWidth} max-h-[90vh] overflow-y-auto p-6`}>
        {title && <h3 className="text-base font-semibold text-slate-800 mb-4">{title}</h3>}
        {children}
      </div>
    </div>
  );
}

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { COMMON_EMOJIS } from '@/lib/emoji';

const FONT_FAMILIES = [
  { name: 'Default', value: 'inherit' },
  { name: 'Inter / Sans-Serif', value: 'Inter, system-ui, sans-serif' },
  { name: 'Serif / Georgia', value: 'Georgia, serif' },
  { name: 'Monospace', value: 'ui-monospace, monospace' },
  { name: 'Comic / Casual', value: 'cursive' }
];

const FONT_SIZES = [
  { name: 'Small (12px)', value: '1' },
  { name: 'Normal (14px)', value: '3' },
  { name: 'Medium (16px)', value: '4' },
  { name: 'Large (18px)', value: '5' },
  { name: 'Extra Large (24px)', value: '6' },
  { name: 'Huge (32px)', value: '7' }
];

const TEXT_COLORS = [
  '#000000', '#475569', '#dc2626', '#d97706', '#059669', '#2563eb', '#7c3aed', '#db2777', '#ffffff'
];

const HIGHLIGHT_COLORS = [
  'transparent', '#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#cbd5e1'
];

export { COMMON_EMOJIS };

export const RichTextEditor = ({
  value,
  onChange,
  label = 'Service Features & Highlights (Rich Text Editor)',
  placeholder = 'Write detailed service features, benefits, specifications and terms...',
  minHeight = '220px',
  className = ''
}) => {
  const editorRef = useRef(null);
  const editorWrapRef = useRef(null);
  const savedRangeRef = useRef(null);
  const lastPushedHtmlRef = useRef(value || '');
  const fileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState('visual');
  const [htmlCode, setHtmlCode] = useState(value || '');
  const [imageToolbarPos, setImageToolbarPos] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [tableToolbarPos, setTableToolbarPos] = useState(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  // Link Modal
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');

  // Image Modal
  const [isImgModalOpen, setIsImgModalOpen] = useState(false);
  const [imgMode, setImgMode] = useState('url');
  const [imgUrl, setImgUrl] = useState('');
  const [imgAlt, setImgAlt] = useState('');
  const [imgWidth, setImgWidth] = useState('100');
  const [imgHeight, setImgHeight] = useState('auto');
  const [imgAlign, setImgAlign] = useState('center');
  const [editingImage, setEditingImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Table Modal
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [includeTableHeader, setIncludeTableHeader] = useState(true);

  const decorateEditorMedia = () => {
    if (!editorRef.current) return;
    editorRef.current.querySelectorAll('img').forEach((img) => {
      img.setAttribute('contenteditable', 'false');
      img.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
      img.draggable = true;
      img.classList.add('rte-image');
      if (!img.getAttribute('alt')) img.setAttribute('alt', 'Service image');
      img.style.maxWidth = '100%';
      if (!img.style.height) img.style.height = 'auto';
      img.style.visibility = 'visible';
      img.style.opacity = '1';
      if (!img.style.display) {
        img.style.display = 'block';
      }
    });
    editorRef.current.querySelectorAll('table').forEach((table) => {
      if (!table.style.width) table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.display = 'table';
      table.style.visibility = 'visible';
    });
  };

  const emitFromEditor = () => {
    if (!editorRef.current) return;
    decorateEditorMedia();
    const rawContent = editorRef.current.innerHTML;
    const content = sanitizeHtml(rawContent);
    if (content !== rawContent) {
      const currentImages = Array.from(editorRef.current.querySelectorAll('img'));
      const currentTables = Array.from(editorRef.current.querySelectorAll('table'));
      const selectedImageIndex = selectedImage ? currentImages.indexOf(selectedImage) : -1;
      const selectedTableIndex = selectedTable ? currentTables.indexOf(selectedTable) : -1;
      const selectedCellIndex = selectedTable && selectedCell
        ? Array.from(selectedTable.querySelectorAll('td, th')).indexOf(selectedCell)
        : -1;
      editorRef.current.innerHTML = content;
      decorateEditorMedia();
      const sanitizedImages = Array.from(editorRef.current.querySelectorAll('img'));
      const sanitizedTables = Array.from(editorRef.current.querySelectorAll('table'));
      const nextSelectedImage = sanitizedImages[selectedImageIndex] || null;
      const nextSelectedTable = sanitizedTables[selectedTableIndex] || null;
      const nextSelectedCell = nextSelectedTable
        ? Array.from(nextSelectedTable.querySelectorAll('td, th'))[selectedCellIndex] || null
        : null;
      nextSelectedImage?.classList.add('rte-image-selected');
      nextSelectedTable?.classList.add('rte-table-selected');
      setSelectedImage(nextSelectedImage);
      setSelectedTable(nextSelectedTable);
      setSelectedCell(nextSelectedCell);
    }
    lastPushedHtmlRef.current = content;
    setHtmlCode(content);
    onChange(content);
  };

  // Sync from parent when the value changes from outside this editor
  useLayoutEffect(() => {
    const incoming = sanitizeHtml(value || '');
    if (activeTab !== 'visual' || !editorRef.current) {
      setHtmlCode(incoming);
      lastPushedHtmlRef.current = incoming;
      return;
    }
    const editorHtml = editorRef.current.innerHTML;
    const externalChange = incoming !== lastPushedHtmlRef.current;
    if (externalChange || (!editorHtml && incoming)) {
      editorRef.current.innerHTML = incoming;
      decorateEditorMedia();
    }
    lastPushedHtmlRef.current = incoming;
    setHtmlCode(incoming);
  }, [value, activeTab]);

  const handleEditorInput = () => {
    emitFromEditor();
  };

  const handleCodeChange = (e) => {
    const newCode = e.target.value;
    lastPushedHtmlRef.current = newCode;
    setHtmlCode(newCode);
    onChange(newCode);
  };

  // Save selection whenever cursor moves inside the editor
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current) {
      const range = sel.getRangeAt(0);
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        savedRangeRef.current = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (!sel || !savedRangeRef.current || !editorRef.current) return false;
    try {
      if (!editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)) return false;
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
      return true;
    } catch {
      return false;
    }
  };

  const placeCaretAfter = (node) => {
    const sel = window.getSelection();
    if (!sel || !editorRef.current) return;
    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    savedRangeRef.current = range.cloneRange();
  };

  const getEditorRange = () => {
    if (!editorRef.current) return null;
    editorRef.current.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current.contains(sel.getRangeAt(0).commonAncestorContainer)) {
      return sel.getRangeAt(0);
    }
    if (savedRangeRef.current) {
      try {
        if (editorRef.current.contains(savedRangeRef.current.commonAncestorContainer)) {
          return savedRangeRef.current.cloneRange();
        }
      } catch {
        // stale range
      }
    }
    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    return range;
  };

  const insertHtmlAtCursor = (htmlToInsert, asBlock = false) => {
    if (!editorRef.current) return;
    const temp = document.createElement('div');
    temp.innerHTML = htmlToInsert;
    const nodes = Array.from(temp.childNodes);
    if (nodes.length === 0) {
      editorRef.current.insertAdjacentHTML('beforeend', htmlToInsert);
      emitFromEditor();
      return;
    }

    const range = getEditorRange();
    if (!range) {
      nodes.forEach((n) => editorRef.current?.appendChild(n));
      emitFromEditor();
      return;
    }

    if (asBlock) {
      let ancestor = range.commonAncestorContainer;
      if (ancestor.nodeType === Node.TEXT_NODE) ancestor = ancestor.parentNode;
      const block = ancestor?.closest?.(
        'p, h1, h2, h3, h4, h5, h6, pre, blockquote, li, span, font, a'
      );
      if (block && editorRef.current.contains(block) && block !== editorRef.current) {
        let insertAfter = block;
        nodes.forEach((n) => {
          insertAfter.parentNode?.insertBefore(n, insertAfter.nextSibling);
          insertAfter = n;
        });
        placeCaretAfter(insertAfter);
        emitFromEditor();
        return;
      }
    }

    range.deleteContents();
    const frag = document.createDocumentFragment();
    let lastNode = null;
    nodes.forEach((n) => {
      lastNode = frag.appendChild(n);
    });
    range.insertNode(frag);
    if (lastNode) placeCaretAfter(lastNode);
    emitFromEditor();
  };

  // Helper function to execute document.execCommand commands
  const execCmd = (command, cmdValue = '') => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, cmdValue);
    handleEditorInput();
    saveSelection();
  };

  // Heading formatting
  const handleHeadingSelect = (e) => {
    const val = e.target.value;
    if (val === 'p') {
      execCmd('formatBlock', '<p>');
    } else if (val.startsWith('h')) {
      execCmd('formatBlock', `<${val}>`);
    }
  };

  // Tag wrapping helper for custom elements like <del>, <ins>, <mark>
  const wrapSelectionWithTag = (tagName, attributes = '') => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (!selectedText) {
      const el = document.createElement(tagName);
      if (attributes) el.setAttribute('style', attributes);
      el.innerHTML = '&#8203;'; // zero width space
      range.insertNode(el);
    } else {
      const el = document.createElement(tagName);
      if (attributes) el.setAttribute('style', attributes);
      el.appendChild(range.extractContents());
      range.insertNode(el);
    }
    handleEditorInput();
    saveSelection();
  };

  const resetImageModal = () => {
    setIsImgModalOpen(false);
    setImgUrl('');
    setImgAlt('');
    setImgWidth('100');
    setImgHeight('auto');
    setImgAlign('center');
    setImgMode('url');
    setEditingImage(null);
  };

  const openLinkModal = () => {
    saveSelection();
    setIsLinkModalOpen(true);
  };

  const openImgModal = () => {
    saveSelection();
    setEditingImage(null);
    setImgUrl('');
    setImgAlt('');
    setImgWidth('100');
    setImgHeight('auto');
    setImgAlign('center');
    setImgMode('url');
    setIsImgModalOpen(true);
  };

  const openImageEditModal = (img) => {
    saveSelection();
    setEditingImage(img);
    setSelectedImage(img);
    setImgUrl(img.getAttribute('src') || '');
    setImgAlt(img.getAttribute('alt') || '');
    const widthAttr = img.getAttribute('width') || img.style.width || '100%';
    const numeric = String(widthAttr).replace(/[^0-9.]/g, '');
    setImgWidth(numeric || '100');
    const heightAttr = img.getAttribute('height') || img.style.height || 'auto';
    const numericHeight = String(heightAttr).replace(/[^0-9.]/g, '');
    setImgHeight(numericHeight || 'auto');
    let detectedAlign = 'center';
    if (img.style.float === 'left') detectedAlign = 'left';
    else if (img.style.float === 'right') detectedAlign = 'right';
    else if (img.style.width === '100%' || widthAttr === '100%') detectedAlign = 'full';
    else if (img.style.marginLeft === 'auto' || img.parentElement?.style?.textAlign === 'center') detectedAlign = 'center';
    setImgAlign(detectedAlign);
    setImgMode(img.src.startsWith('data:') ? 'upload' : 'url');
    setIsImgModalOpen(true);
  };

  const openTableModal = () => {
    saveSelection();
    setIsTableModalOpen(true);
  };

  // Get the direct child of editor that contains `el` (the moveable block)
  const getMoveableBlock = (el) => {
    if (!editorRef.current) return null;
    let node = el;
    while (node && node.parentElement !== editorRef.current) {
      node = node.parentElement;
    }
    return node && node !== editorRef.current ? node : null;
  };

  const moveBlockUp = (el) => {
    const block = getMoveableBlock(el);
    if (!block || !block.parentElement) return;
    const prev = block.previousElementSibling;
    if (prev) {
      block.parentElement.insertBefore(block, prev);
      emitFromEditor();
      (el).scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const moveBlockDown = (el) => {
    const block = getMoveableBlock(el);
    if (!block || !block.parentElement) return;
    const next = block.nextElementSibling;
    if (next) {
      block.parentElement.insertBefore(block, next.nextElementSibling);
      emitFromEditor();
      (el).scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const setImageWidthPreset = (widthPercent: string) => {
    if (!selectedImage) return;
    selectedImage.style.width = widthPercent;
    emitFromEditor();
  };

  const alignImage = (alignment: 'left' | 'center' | 'right' | 'full') => {
    if (!selectedImage) return;
    const parentBlock = selectedImage.parentElement;

    if (alignment === 'left') {
      const currentWidth = selectedImage.style.width;
      if (!currentWidth || currentWidth === '100%') {
        selectedImage.style.width = '48%';
      }
      selectedImage.style.display = 'block';
      selectedImage.style.float = 'left';
      selectedImage.style.marginLeft = '0px';
      selectedImage.style.marginRight = '1.25rem';
      selectedImage.style.marginTop = '0.25rem';
      selectedImage.style.marginBottom = '0.75rem';
      selectedImage.style.clear = 'none';
      if (parentBlock && parentBlock !== editorRef.current) {
        parentBlock.style.textAlign = 'left';
      }
    } else if (alignment === 'right') {
      const currentWidth = selectedImage.style.width;
      if (!currentWidth || currentWidth === '100%') {
        selectedImage.style.width = '48%';
      }
      selectedImage.style.display = 'block';
      selectedImage.style.float = 'right';
      selectedImage.style.marginLeft = '1.25rem';
      selectedImage.style.marginRight = '0px';
      selectedImage.style.marginTop = '0.25rem';
      selectedImage.style.marginBottom = '0.75rem';
      selectedImage.style.clear = 'none';
      if (parentBlock && parentBlock !== editorRef.current) {
        parentBlock.style.textAlign = 'right';
      }
    } else if (alignment === 'center') {
      const currentWidth = selectedImage.style.width;
      if (!currentWidth || currentWidth === '100%') {
        selectedImage.style.width = '70%';
      }
      selectedImage.style.display = 'block';
      selectedImage.style.float = 'none';
      selectedImage.style.marginLeft = 'auto';
      selectedImage.style.marginRight = 'auto';
      selectedImage.style.marginTop = '0.75rem';
      selectedImage.style.marginBottom = '0.75rem';
      selectedImage.style.clear = 'both';
      if (parentBlock && parentBlock !== editorRef.current) {
        parentBlock.style.textAlign = 'center';
      }
    } else if (alignment === 'full') {
      selectedImage.style.width = '100%';
      selectedImage.style.display = 'block';
      selectedImage.style.float = 'none';
      selectedImage.style.marginLeft = 'auto';
      selectedImage.style.marginRight = 'auto';
      selectedImage.style.marginTop = '0.75rem';
      selectedImage.style.marginBottom = '0.75rem';
      selectedImage.style.clear = 'both';
      if (parentBlock && parentBlock !== editorRef.current) {
        parentBlock.style.textAlign = 'center';
      }
    }
    emitFromEditor();
  };

  const alignTable = (alignment: 'left' | 'center' | 'right' | 'full') => {
    if (!selectedTable) return;
    const wrapper = (selectedTable.parentElement?.style?.overflowX !== undefined && selectedTable.parentElement !== editorRef.current)
      ? selectedTable.parentElement
      : null;

    if (alignment === 'full') {
      selectedTable.style.width = '100%';
      selectedTable.style.marginLeft = '0';
      selectedTable.style.marginRight = '0';
      if (wrapper) {
        wrapper.style.width = '100%';
        wrapper.style.marginLeft = '0';
        wrapper.style.marginRight = '0';
      }
    } else if (alignment === 'center') {
      const w = selectedTable.style.width === '100%' ? '85%' : (selectedTable.style.width || '85%');
      selectedTable.style.width = w;
      selectedTable.style.marginLeft = 'auto';
      selectedTable.style.marginRight = 'auto';
      if (wrapper) {
        wrapper.style.width = w;
        wrapper.style.marginLeft = 'auto';
        wrapper.style.marginRight = 'auto';
      }
    } else if (alignment === 'left') {
      const w = selectedTable.style.width === '100%' ? '85%' : (selectedTable.style.width || '85%');
      selectedTable.style.width = w;
      selectedTable.style.marginLeft = '0';
      selectedTable.style.marginRight = 'auto';
      if (wrapper) {
        wrapper.style.width = w;
        wrapper.style.marginLeft = '0';
        wrapper.style.marginRight = 'auto';
      }
    } else if (alignment === 'right') {
      const w = selectedTable.style.width === '100%' ? '85%' : (selectedTable.style.width || '85%');
      selectedTable.style.width = w;
      selectedTable.style.marginLeft = 'auto';
      selectedTable.style.marginRight = '0';
      if (wrapper) {
        wrapper.style.width = w;
        wrapper.style.marginLeft = 'auto';
        wrapper.style.marginRight = '0';
      }
    }
    emitFromEditor();
  };

  const addTableRow = (below = true) => {
    if (!selectedTable) return;
    const targetRow = selectedCell?.closest('tr') || selectedTable.querySelector('tbody tr') || selectedTable.querySelector('tr');
    if (!targetRow) return;
    const colCount = targetRow.cells.length || 3;
    const newRow = document.createElement('tr');
    for (let i = 0; i < colCount; i++) {
      const td = document.createElement('td');
      td.setAttribute('style', 'padding:0.5rem 0.75rem; border:1px solid rgba(148,163,184,0.4);');
      td.innerHTML = '<br>';
      newRow.appendChild(td);
    }
    if (below) {
      targetRow.parentElement?.insertBefore(newRow, targetRow.nextSibling);
    } else {
      targetRow.parentElement?.insertBefore(newRow, targetRow);
    }
    emitFromEditor();
  };

  const addTableCol = (right = true) => {
    if (!selectedTable) return;
    const colIndex = selectedCell ? selectedCell.cellIndex : (selectedTable.rows[0]?.cells.length ? selectedTable.rows[0].cells.length - 1 : 0);
    const rows = Array.from(selectedTable.rows);
    rows.forEach((row) => {
      const isHeader = row.parentElement?.tagName.toLowerCase() === 'thead' || row.cells[0]?.tagName.toLowerCase() === 'th';
      const cell = document.createElement(isHeader ? 'th' : 'td');
      cell.setAttribute('style', isHeader
        ? 'padding:0.5rem 0.75rem; border:1px solid rgba(148,163,184,0.4); background:rgba(148,163,184,0.18); font-weight:700;'
        : 'padding:0.5rem 0.75rem; border:1px solid rgba(148,163,184,0.4);');
      cell.innerHTML = isHeader ? 'New Header' : '<br>';
      const refCell = row.cells[colIndex];
      if (right) {
        row.insertBefore(cell, refCell ? refCell.nextSibling : null);
      } else {
        row.insertBefore(cell, refCell || null);
      }
    });
    emitFromEditor();
  };

  const deleteTableRow = () => {
    if (!selectedTable) return;
    const targetRow = selectedCell?.closest('tr') || (selectedTable.rows.length > 1 ? selectedTable.rows[selectedTable.rows.length - 1] : null);
    if (targetRow && selectedTable.rows.length > 1) {
      targetRow.remove();
      setSelectedCell(null);
      emitFromEditor();
    }
  };

  const deleteTableCol = () => {
    if (!selectedTable) return;
    const firstRow = selectedTable.rows[0];
    if (!firstRow || firstRow.cells.length <= 1) return;
    const colIndex = selectedCell ? selectedCell.cellIndex : firstRow.cells.length - 1;
    const rows = Array.from(selectedTable.rows);
    rows.forEach((row) => {
      if (row.cells[colIndex]) {
        row.cells[colIndex].remove();
      }
    });
    setSelectedCell(null);
    emitFromEditor();
  };

  const handleEditorClick = (e) => {
    const target = e.target;

    // Image selection
    const img = target.closest('img') | null;
    if (img && editorRef.current?.contains(img)) {
      setSelectedImage(img);
      editorRef.current.querySelectorAll('img').forEach((el) => el.classList.remove('rte-image-selected'));
      img.classList.add('rte-image-selected');
      setSelectedTable(null);
      setSelectedCell(null);
      editorRef.current.querySelectorAll('table').forEach((el) => el.classList.remove('rte-table-selected'));
      return;
    }

    // Table selection
    const table = target.closest('table') | null;
    if (table && editorRef.current?.contains(table)) {
      setSelectedTable(table);
      const cell = target.closest('td, th') | null;
      setSelectedCell(cell);
      editorRef.current.querySelectorAll('table').forEach((el) => el.classList.remove('rte-table-selected'));
      table.classList.add('rte-table-selected');
      setSelectedImage(null);
      editorRef.current.querySelectorAll('img').forEach((el) => el.classList.remove('rte-image-selected'));
      return;
    }

    // Deselect all
    setSelectedImage(null);
    setSelectedTable(null);
    setSelectedCell(null);
    editorRef.current?.querySelectorAll('img').forEach((el) => el.classList.remove('rte-image-selected'));
    editorRef.current?.querySelectorAll('table').forEach((el) => el.classList.remove('rte-table-selected'));
  };

  useEffect(() => {
    if (!selectedImage || !editorWrapRef.current || !editorRef.current?.contains(selectedImage)) {
      setImageToolbarPos(null);
      return;
    }
    const updatePos = () => {
      if (!selectedImage || !editorWrapRef.current) return;
      const img = selectedImage.getBoundingClientRect();
      const wrap = editorWrapRef.current.getBoundingClientRect();
      setImageToolbarPos({
        top: Math.max(4, img.top - wrap.top - 44),
        left: Math.max(4, Math.min(wrap.width - 340, img.left - wrap.left))
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('resize', updatePos);
    };
  }, [selectedImage, htmlCode]);

  // Track table toolbar position
  useEffect(() => {
    if (!selectedTable || !editorWrapRef.current || !editorRef.current?.contains(selectedTable)) {
      setTableToolbarPos(null);
      return;
    }
    const updatePos = () => {
      if (!selectedTable || !editorWrapRef.current) return;
      const rect = selectedTable.getBoundingClientRect();
      const wrap = editorWrapRef.current.getBoundingClientRect();
      setTableToolbarPos({
        top: Math.max(4, rect.top - wrap.top - 44),
        left: Math.max(4, Math.min(wrap.width - 340, rect.left - wrap.left))
      });
    };
    updatePos();
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('resize', updatePos);
    };
  }, [selectedTable, htmlCode]);

  const handleEditorDblClick = (e) => {
    const target = e.target;
    const img = target.closest('img');
    if (img && editorRef.current?.contains(img)) {
      e.preventDefault();
      openImageEditModal(img);
    }
  };

  const handleRemoveSelectedImage = () => {
    if (!selectedImage || !editorRef.current?.contains(selectedImage)) return;
    selectedImage.remove();
    setSelectedImage(null);
    handleEditorInput();
  };

  // Link Insertion
  const handleInsertLink = () => {
    if (!linkUrl.trim()) return;
    const finalUrl = normalizeSafeUrl(linkUrl.trim()) || (linkUrl.startsWith('http') || linkUrl.startsWith('mailto:') ? linkUrl.trim() : `https://${linkUrl.trim()}`);
    const textToInsert = linkText.trim() || finalUrl;
    const anchorHtml = `<a href="${finalUrl}" target="_blank" rel="noopener noreferrer">${textToInsert}</a>&nbsp;`;
    insertHtmlAtCursor(anchorHtml);
    setIsLinkModalOpen(false);
    setLinkUrl('');
    setLinkText('');
    savedRangeRef.current = null;
  };

  const buildImageHtml = (src: string, alt: string, widthPercent: string, align: 'left' | 'center' | 'right' | 'full' = 'center', height: string = 'auto') => {
    const safeSrc = (normalizeSafeUrl(src) || src).replace(/"/g, '&quot;');
    const safeAlt = alt.replace(/"/g, '&quot;');
    const width = Math.max(10, Math.min(100, parseInt(widthPercent, 10) || 100));
    const safeHeight = /^\d{1,4}$/.test(height) ? `${Math.max(20, Math.min(1200, Number(height)))}px` : 'auto';
    let style = `width: ${width}%; max-width: 100%; height: ${safeHeight}; display: block;`;
    if (align === 'center') {
      style += ` margin-left: auto; margin-right: auto; float: none; clear: both; margin-top: 0.75rem; margin-bottom: 0.75rem;`;
    } else if (align === 'left') {
      style += ` float: left; margin-right: 1.25rem; margin-bottom: 0.75rem; margin-top: 0.25rem; margin-left: 0; clear: none;`;
    } else if (align === 'right') {
      style += ` float: right; margin-left: 1.25rem; margin-bottom: 0.75rem; margin-top: 0.25rem; margin-right: 0; clear: none;`;
    } else if (align === 'full') {
      style = `width: 100%; max-width: 100%; height: ${safeHeight}; display: block; margin-left: auto; margin-right: auto; float: none; clear: both; margin-top: 0.75rem; margin-bottom: 0.75rem;`;
    }
    return `<img src="${safeSrc}" alt="${safeAlt}" referrerpolicy="no-referrer-when-downgrade" class="rte-image" style="${style}" />`;
  };

  const handleInsertImage = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!imgUrl.trim()) return;
    const finalUrl = normalizeSafeUrl(imgUrl.trim()) || imgUrl.trim();
    const altText = imgAlt.trim() || 'Service Feature';

    if (editingImage && editorRef.current?.contains(editingImage)) {
      editingImage.setAttribute('src', finalUrl);
      editingImage.setAttribute('alt', altText);
      editingImage.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
      const width = Math.max(10, Math.min(100, parseInt(imgWidth, 10) || 100));
      editingImage.removeAttribute('width');
      editingImage.style.width = `${width}%`;
      editingImage.style.maxWidth = '100%';
      editingImage.style.height = /^\d{1,4}$/.test(imgHeight)
        ? `${Math.max(20, Math.min(1200, Number(imgHeight)))}px`
        : 'auto';
      editingImage.style.display = 'block';

      if (imgAlign === 'left') {
        editingImage.style.float = 'left';
        editingImage.style.marginLeft = '0px';
        editingImage.style.marginRight = '1.25rem';
        editingImage.style.marginTop = '0.25rem';
        editingImage.style.marginBottom = '0.75rem';
        editingImage.style.clear = 'none';
        if (editingImage.parentElement && editingImage.parentElement !== editorRef.current) {
          editingImage.parentElement.style.textAlign = 'left';
        }
      } else if (imgAlign === 'right') {
        editingImage.style.float = 'right';
        editingImage.style.marginLeft = '1.25rem';
        editingImage.style.marginRight = '0px';
        editingImage.style.marginTop = '0.25rem';
        editingImage.style.marginBottom = '0.75rem';
        editingImage.style.clear = 'none';
        if (editingImage.parentElement && editingImage.parentElement !== editorRef.current) {
          editingImage.parentElement.style.textAlign = 'right';
        }
      } else if (imgAlign === 'center') {
        editingImage.style.float = 'none';
        editingImage.style.marginLeft = 'auto';
        editingImage.style.marginRight = 'auto';
        editingImage.style.marginTop = '0.75rem';
        editingImage.style.marginBottom = '0.75rem';
        editingImage.style.clear = 'both';
        if (editingImage.parentElement && editingImage.parentElement !== editorRef.current) {
          editingImage.parentElement.style.textAlign = 'center';
        }
      } else if (imgAlign === 'full') {
        editingImage.style.width = '100%';
        editingImage.style.float = 'none';
        editingImage.style.marginLeft = 'auto';
        editingImage.style.marginRight = 'auto';
        editingImage.style.marginTop = '0.75rem';
        editingImage.style.marginBottom = '0.75rem';
        editingImage.style.clear = 'both';
        if (editingImage.parentElement && editingImage.parentElement !== editorRef.current) {
          editingImage.parentElement.style.textAlign = 'center';
        }
      }

      emitFromEditor();
      resetImageModal();
      savedRangeRef.current = null;
      return;
    }

    const pStyle = imgAlign === 'center' ? ' style="text-align: center;"' : (imgAlign === 'right' ? ' style="text-align: right;"' : '');
    insertHtmlAtCursor(`<p${pStyle}>${buildImageHtml(finalUrl, altText, imgWidth, imgAlign, imgHeight)}</p><p><br></p>`, true);
    resetImageModal();
    savedRangeRef.current = null;
  };

  // File Upload Handler for Images
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert('Image file size must be less than 3MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setImgUrl(reader.result);
        if (!imgAlt) {
          setImgAlt(file.name.replace(/\.[^/.]+$/, ''));
        }
      }
    };
    reader.readAsDataURL(file);
  };

  // Table Insertion
  const handleInsertTable = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const rows = Math.max(1, tableRows);
    const cols = Math.max(1, tableCols);
    // Use style overflow-x on div (kept by sanitizer) for horizontal scroll wrapper
    let tableHtml = '<div style="width:100%; overflow-x:auto; margin-top:0.75rem; margin-bottom:0.75rem;"><table border="1" style="width:100%; border-collapse:collapse; display:table; min-width:240px;">';

    if (includeTableHeader) {
      tableHtml += '<thead><tr>';
      for (let c = 0; c < cols; c++) {
        tableHtml += `<th style="padding:0.5rem 0.75rem; border:1px solid rgba(148,163,184,0.4); background:rgba(148,163,184,0.18); font-weight:700;">Header ${c + 1}</th>`;
      }
      tableHtml += '</tr></thead>';
    }

    tableHtml += '<tbody>';
    const bodyRowCount = includeTableHeader ? Math.max(1, rows - 1) : rows;
    for (let r = 0; r < bodyRowCount; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        tableHtml += `<td style="padding:0.5rem 0.75rem; border:1px solid rgba(148,163,184,0.35);">Data ${r + 1}-${c + 1}</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table></div><p><br></p>';

    insertHtmlAtCursor(tableHtml, true);
    setIsTableModalOpen(false);
    savedRangeRef.current = null;
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
          <label className="font-bold text-slate-900 dark:text-slate-100 text-xs flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-brand-600 dark:text-brand-400" />
            <span>{label}</span>
          </label>

          {/* Mode Switch Tabs */}
          <div className="flex max-w-full flex-wrap items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab('visual')}
              className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors flex items-center gap-1 ${
                activeTab === 'visual'
                  ? 'bg-white text-brand-600 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Edit3 className="w-3 h-3" /> Visual
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('code')}
              className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors flex items-center gap-1 ${
                activeTab === 'code'
                  ? 'bg-white text-brand-600 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Code2 className="w-3 h-3" /> HTML Code
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('preview')}
              className={`px-2.5 py-1 text-[11px] font-bold rounded-md transition-colors flex items-center gap-1 ${
                activeTab === 'preview'
                  ? 'bg-white text-brand-600 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <Eye className="w-3 h-3" /> Live Preview
            </button>
          </div>
        </div>
      )}

      {/* Editor Container - overflow visible so floating image toolbar isn't clipped */}
      <div className={`border border-slate-300 rounded-2xl bg-white shadow-subtle flex flex-col focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-500 transition-all`}>
        
        {/* Contextual Toolbar for Selected Image */}
        {activeTab === 'visual' && selectedImage && (
          <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 flex flex-wrap items-center justify-between gap-2 text-xs select-none">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-blue-700 flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> Image Controls:
              </span>
              {/* Alignment */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-blue-200">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignImage('left')}
                  title="Float Left (Text wraps around)"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 flex items-center gap-1 text-slate-700"
                >
                  <AlignLeft className="w-3.5 h-3.5" /> Left
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignImage('center')}
                  title="Center Image"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 flex items-center gap-1 text-slate-700"
                >
                  <AlignCenter className="w-3.5 h-3.5" /> Center
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignImage('right')}
                  title="Float Right (Text wraps around)"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 flex items-center gap-1 text-slate-700"
                >
                  <AlignRight className="w-3.5 h-3.5" /> Right
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignImage('full')}
                  title="Full Width (100%)"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 flex items-center gap-1 text-slate-700"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Full
                </button>
              </div>

              {/* Size Presets */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-blue-200">
                <span className="text-[10px] text-slate-400 font-semibold px-1">Size:</span>
                {(['25%', '50%', '75%', '100%']).map((w) => (
                  <button
                    key={w}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setImageWidthPreset(w)}
                    className="px-1.5 py-0.5 text-[10px] font-bold rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 text-slate-700 dark:text-slate-200"
                  >
                    {w}
                  </button>
                ))}
              </div>

              {/* Move block */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-blue-200">
                <span className="text-[10px] text-slate-400 font-semibold px-1">Move:</span>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => moveBlockUp(selectedImage)}
                  title="Move Image Up"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 flex items-center gap-1 text-slate-700 dark:text-slate-200"
                >
                  <ArrowUp className="w-3.5 h-3.5" /> Up
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => moveBlockDown(selectedImage)}
                  title="Move Image Down"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-blue-100 dark:hover:bg-blue-900/50 flex items-center gap-1 text-slate-700 dark:text-slate-200"
                >
                  <ArrowDown className="w-3.5 h-3.5" /> Down
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openImageEditModal(selectedImage)}
                className="px-2 py-1 bg-white dark:bg-slate-900 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-xs font-semibold flex items-center gap-1"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleRemoveSelectedImage}
                className="px-2 py-1 bg-white dark:bg-slate-900 rounded-lg border border-rose-200 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-semibold flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSelectedImage(null);
                  editorRef.current?.querySelectorAll('img').forEach((el) => el.classList.remove('rte-image-selected'));
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                title="Deselect"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Contextual Toolbar for Selected Table */}
        {activeTab === 'visual' && selectedTable && (
          <div className="px-3 py-2 bg-indigo-50 border-b border-indigo-200 flex flex-wrap items-center justify-between gap-2 text-xs select-none">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
                <Table2 className="w-3.5 h-3.5" /> Table Controls:
              </span>

              {/* Move block */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-indigo-200">
                <span className="text-[10px] text-slate-400 font-semibold px-1">Move:</span>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => moveBlockUp(selectedTable)}
                  title="Move Table Up"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 flex items-center gap-1 text-slate-700 dark:text-slate-200"
                >
                  <ArrowUp className="w-3.5 h-3.5" /> Up
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => moveBlockDown(selectedTable)}
                  title="Move Table Down"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 flex items-center gap-1 text-slate-700 dark:text-slate-200"
                >
                  <ArrowDown className="w-3.5 h-3.5" /> Down
                </button>
              </div>

              {/* Row & Column adjustments */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-indigo-200">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addTableRow(true)}
                  title="Add Row Below"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-slate-700 dark:text-slate-200 flex items-center gap-0.5"
                >
                  <Plus className="w-3 h-3" /> Row
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addTableCol(true)}
                  title="Add Column Right"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-slate-700 dark:text-slate-200 flex items-center gap-0.5"
                >
                  <Plus className="w-3 h-3" /> Col
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={deleteTableRow}
                  title="Delete Row"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400"
                >
                  - Row
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={deleteTableCol}
                  title="Delete Column"
                  className="px-2 py-1 text-[11px] font-semibold rounded hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400"
                >
                  - Col
                </button>
              </div>

              {/* Alignment */}
              <div className="flex items-center gap-0.5 bg-white rounded-lg p-0.5 border border-indigo-200">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignTable('left')}
                  title="Align Table Left"
                  className="p-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-slate-700 dark:text-slate-200"
                >
                  <AlignLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignTable('center')}
                  title="Align Table Center"
                  className="p-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-slate-700 dark:text-slate-200"
                >
                  <AlignCenter className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => alignTable('full')}
                  title="Full Width Table"
                  className="p-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-slate-700 dark:text-slate-200"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Delete Table & Close */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  const block = getMoveableBlock(selectedTable) || selectedTable;
                  block.remove();
                  setSelectedTable(null);
                  setSelectedCell(null);
                  handleEditorInput();
                }}
                className="px-2 py-1 bg-white dark:bg-slate-900 rounded-lg border border-rose-200 dark:border-rose-900/50 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 text-xs font-semibold flex items-center gap-1"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete Table
              </button>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSelectedTable(null);
                  setSelectedCell(null);
                  editorRef.current?.querySelectorAll('table').forEach((el) => el.classList.remove('rte-table-selected'));
                }}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                title="Deselect"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* TOOLBAR (Only shown in Visual Mode) */}
        {activeTab === 'visual' && (
          <div
            className="p-2 bg-white border-b border-slate-200 flex flex-wrap items-center gap-1.5 text-xs select-none"
            onMouseDown={(e) => e.preventDefault()}
          >
            
            {/* Headings Dropdown */}
            <select
              onChange={handleHeadingSelect}
              defaultValue="p"
              title="Heading Style"
              className="p-1.5 bg-white text-slate-800 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none"
            >
              <option value="p">Paragraph</option>
              <option value="h1">Heading 1 (H1)</option>
              <option value="h2">Heading 2 (H2)</option>
              <option value="h3">Heading 3 (H3)</option>
              <option value="h4">Heading 4 (H4)</option>
              <option value="h5">Heading 5 (H5)</option>
              <option value="h6">Heading 6 (H6)</option>
            </select>

            {/* Font Family */}
            <select
              onChange={(e) => execCmd('fontName', e.target.value)}
              defaultValue="inherit"
              title="Font Family"
              className="p-1.5 bg-white text-slate-800 rounded-lg border border-slate-200 text-xs focus:outline-none max-w-[110px] truncate"
            >
              {FONT_FAMILIES.map(f => (
                <option key={f.name} value={f.value}>{f.name}</option>
              ))}
            </select>

            {/* Font Size */}
            <select
              onChange={(e) => execCmd('fontSize', e.target.value)}
              defaultValue="3"
              title="Font Size"
              className="p-1.5 bg-white text-slate-800 rounded-lg border border-slate-200 text-xs focus:outline-none"
            >
              {FONT_SIZES.map(s => (
                <option key={s.name} value={s.value}>{s.name}</option>
              ))}
            </select>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Text Formatting Buttons */}
            <button
              type="button"
              onClick={() => execCmd('bold')}
              title="Bold (<strong>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('italic')}
              title="Italic (<em>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('underline')}
              title="Underline (<u>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Underline className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('strikeThrough')}
              title="Strikethrough (<s>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Strikethrough className="w-4 h-4" />
            </button>

            {/* Del, Ins, Mark tag buttons */}
            <button
              type="button"
              onClick={() => wrapSelectionWithTag('del')}
              title="Deleted Text (<del>)"
              className="px-1.5 py-1 text-[11px] font-bold rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors border border-rose-200 dark:border-rose-900/50"
            >
              &lt;del&gt;
            </button>
            <button
              type="button"
              onClick={() => wrapSelectionWithTag('ins')}
              title="Inserted Text (<ins>)"
              className="px-1.5 py-1 text-[11px] font-bold rounded-lg text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 transition-colors border border-emerald-200 dark:border-emerald-900/50"
            >
              &lt;ins&gt;
            </button>
            <button
              type="button"
              onClick={() => wrapSelectionWithTag('mark')}
              title="Highlight (<mark>)"
              className="px-1.5 py-1 text-[11px] font-bold rounded-lg bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-100 hover:opacity-90 transition-colors"
            >
              Mark
            </button>

            {/* Subscript & Superscript */}
            <button
              type="button"
              onClick={() => execCmd('subscript')}
              title="Subscript (<sub>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Subscript className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('superscript')}
              title="Superscript (<sup>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Superscript className="w-4 h-4" />
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Text Color Picker */}
            <div className="relative flex items-center gap-1" title="Text Color">
              <Palette className="w-3.5 h-3.5 text-slate-500" />
              <input
                type="color"
                onChange={(e) => execCmd('foreColor', e.target.value)}
                className="w-6 h-6 rounded border border-slate-300 dark:border-slate-700 cursor-pointer bg-transparent"
                title="Choose Text Color"
              />
            </div>

            {/* Highlight Background Color */}
            <div className="relative flex items-center gap-1" title="Background Color">
              <Highlighter className="w-3.5 h-3.5 text-slate-500" />
              <input
                type="color"
                onChange={(e) => execCmd('hiliteColor', e.target.value)}
                className="w-6 h-6 rounded border border-slate-300 dark:border-slate-700 cursor-pointer bg-transparent"
                title="Choose Background Highlight Color"
              />
            </div>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Alignment */}
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (selectedImage) {
                  alignImage('left');
                } else if (selectedTable) {
                  alignTable('left');
                } else {
                  execCmd('justifyLeft');
                }
              }}
              title="Align Left"
              className={`p-1.5 rounded-lg transition-colors ${
                (selectedImage && (selectedImage.style.float === 'left' || selectedImage.parentElement?.style.textAlign === 'left'))
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
              }`}
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (selectedImage) {
                  alignImage('center');
                } else if (selectedTable) {
                  alignTable('center');
                } else {
                  execCmd('justifyCenter');
                }
              }}
              title="Align Center"
              className={`p-1.5 rounded-lg transition-colors ${
                (selectedImage && (selectedImage.style.marginLeft === 'auto' || selectedImage.parentElement?.style.textAlign === 'center'))
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
              }`}
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (selectedImage) {
                  alignImage('right');
                } else if (selectedTable) {
                  alignTable('right');
                } else {
                  execCmd('justifyRight');
                }
              }}
              title="Align Right"
              className={`p-1.5 rounded-lg transition-colors ${
                (selectedImage && (selectedImage.style.float === 'right' || selectedImage.parentElement?.style.textAlign === 'right'))
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800'
              }`}
            >
              <AlignRight className="w-4 h-4" />
            </button>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (selectedImage) {
                  alignImage('full');
                } else if (selectedTable) {
                  alignTable('full');
                } else {
                  execCmd('justifyFull');
                }
              }}
              title="Justify / Full Width"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <AlignJustify className="w-4 h-4" />
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Lists & Indent */}
            <button
              type="button"
              onClick={() => execCmd('insertUnorderedList')}
              title="Bullet List (<ul>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('insertOrderedList')}
              title="Numbered List (<ol>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <ListOrdered className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('indent')}
              title="Indent (Nested List)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Indent className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('outdent')}
              title="Outdent"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Outdent className="w-4 h-4" />
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Block Elements */}
            <button
              type="button"
              onClick={() => execCmd('formatBlock', '<blockquote>')}
              title="Quote (<blockquote>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Quote className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('formatBlock', '<pre>')}
              title="Code Block (<pre>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Code className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => execCmd('insertHorizontalRule')}
              title="Horizontal Line (<hr>)"
              className="p-1.5 rounded-lg text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>

            <div className="h-5 w-px bg-slate-200 dark:bg-slate-800 my-auto mx-0.5" />

            {/* Link & Image Modals */}
            <button
              type="button"
              onClick={openLinkModal}
              title="Insert Link (<a>)"
              className="p-1.5 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors"
            >
              <LinkIcon className="w-4 h-4" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  saveSelection();
                  setIsEmojiPickerOpen(open => !open);
                }}
                title="Insert emoji"
                aria-label="Insert emoji"
                aria-expanded={isEmojiPickerOpen}
                className="rounded-lg p-1.5 text-slate-700 transition-colors hover:bg-slate-200"
              >
                <span aria-hidden="true" className="text-base leading-none">😀</span>
              </button>
              {isEmojiPickerOpen && (
                <div className="absolute left-0 top-full z-[60] mt-1 grid max-h-56 w-52 grid-cols-8 gap-1 overflow-y-auto overscroll-contain rounded-lg border border-slate-200 bg-white p-2 shadow-xl">
                  {COMMON_EMOJIS.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        insertHtmlAtCursor(emoji);
                        setIsEmojiPickerOpen(false);
                      }}
                      aria-label={`Insert ${emoji} emoji`}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-lg leading-none hover:bg-slate-100"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={openImgModal}
              title="Insert Image (<img>)"
              className="p-1.5 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors flex items-center gap-1 font-bold text-xs"
            >
              <ImageIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => selectedImage && openImageEditModal(selectedImage)}
              disabled={!selectedImage}
              title="Edit selected image"
              className="p-1.5 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors flex items-center gap-1 font-bold text-xs disabled:opacity-40 disabled:pointer-events-none"
            >
              <Pencil className="w-4 h-4" />
              Edit image
            </button>
            <button
              type="button"
              onClick={handleRemoveSelectedImage}
              disabled={!selectedImage}
              title="Remove selected image"
              className="p-1.5 rounded-lg text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={openTableModal}
              title="Insert Table (<table>)"
              className="p-1.5 rounded-lg text-brand-600 dark:text-brand-400 hover:bg-brand-50 dark:hover:bg-brand-950/40 transition-colors flex items-center gap-1 font-bold text-xs"
            >
              <Table2 className="w-4 h-4" /> Table
            </button>

          </div>
        )}

        {/* EDITOR AREA */}
        <div className="p-4 flex-1 relative" ref={editorWrapRef}>
          {activeTab === 'visual' && (
            <>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyUp={saveSelection}
                onMouseUp={saveSelection}
                onSelect={saveSelection}
                onClick={handleEditorClick}
                onDoubleClick={handleEditorDblClick}
                onPaste={(e) => {
                  const file = e.clipboardData?.files?.[0];
                  if (file && file.type.startsWith('image/')) {
                    e.preventDefault();
                    if (file.size > 3 * 1024 * 1024) {
                      alert('Image file size must be less than 3MB');
                      return;
                    }
                    const reader = new FileReader();
                    reader.onload = () => {
                      if (typeof reader.result === 'string') {
                        insertHtmlAtCursor(`<p>${buildImageHtml(reader.result, file.name.replace(/\.[^/.]+$/, ''), '100')}</p><p><br></p>`, true);
                      }
                    };
                    reader.readAsDataURL(file);
                  }
                }}
                onBlur={saveSelection}
                style={{ minHeight }}
                className="rich-text-content outline-none text-xs sm:text-sm text-slate-800 leading-relaxed font-normal"
                data-placeholder={placeholder}
              />
              {/* Floating IMAGE toolbar */}
              {selectedImage && imageToolbarPos && (
                <div
                  className="rte-floating-toolbar rte-image-toolbar absolute z-[50] flex flex-wrap items-center gap-0.5 rounded-xl bg-slate-900 text-white shadow-2xl px-1.5 py-1 border border-slate-700"
                  style={{
                    top: imageToolbarPos.top,
                    left: imageToolbarPos.left
                  }}
                >
                  {/* Alignment */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignImage('left')}
                    title="Float Left" className="px-1.5 py-1 text-[11px] font-semibold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <AlignLeft className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignImage('center')}
                    title="Center" className="px-1.5 py-1 text-[11px] font-semibold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <AlignCenter className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignImage('right')}
                    title="Float Right" className="px-1.5 py-1 text-[11px] font-semibold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <AlignRight className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignImage('full')}
                    title="Full Width" className="px-1.5 py-1 text-[11px] font-semibold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <Maximize2 className="w-3 h-3" />
                  </button>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Quick Sizes */}
                  {(['25%', '50%', '75%', '100%']).map((w) => (
                    <button
                      key={w}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setImageWidthPreset(w)}
                      className="px-1 py-0.5 text-[10px] font-bold rounded hover:bg-slate-700 text-slate-300"
                    >
                      {w}
                    </button>
                  ))}
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Move */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveBlockUp(selectedImage)}
                    title="Move Up" className="px-1.5 py-1 text-[11px] rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveBlockDown(selectedImage)}
                    title="Move Down" className="px-1.5 py-1 text-[11px] rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <ArrowDown className="w-3 h-3" />
                  </button>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Edit & Remove */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => openImageEditModal(selectedImage)}
                    className="px-2 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center gap-1 text-blue-300">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={handleRemoveSelectedImage}
                    className="px-2 py-1 text-[11px] font-bold rounded-md hover:bg-rose-700 flex items-center gap-1 text-rose-300">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Floating TABLE toolbar */}
              {selectedTable && tableToolbarPos && (
                <div
                  className="rte-floating-toolbar rte-table-toolbar absolute z-[50] flex flex-wrap items-center gap-0.5 rounded-xl bg-slate-900 text-white shadow-2xl px-1.5 py-1 border border-slate-700"
                  style={{
                    top: tableToolbarPos.top,
                    left: tableToolbarPos.left
                  }}
                >
                  <span className="px-1.5 text-[10px] font-bold text-indigo-400 uppercase tracking-wide">Table</span>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Move */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveBlockUp(selectedTable)}
                    title="Move Table Up" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" /> Up
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => moveBlockDown(selectedTable)}
                    title="Move Table Down" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center gap-1">
                    <ArrowDown className="w-3 h-3" /> Down
                  </button>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Rows & Columns */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addTableRow(true)}
                    title="Add Row Below" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center gap-0.5 text-emerald-300">
                    <Plus className="w-3 h-3" /> Row
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => addTableCol(true)}
                    title="Add Column Right" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center gap-0.5 text-emerald-300">
                    <Plus className="w-3 h-3" /> Col
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={deleteTableRow}
                    title="Delete Row" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center text-rose-300">
                    - Row
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={deleteTableCol}
                    title="Delete Column" className="px-1.5 py-1 text-[11px] font-bold rounded-md hover:bg-slate-700 flex items-center text-rose-300">
                    - Col
                  </button>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Align */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignTable('left')}
                    title="Align Left" className="p-1 rounded hover:bg-slate-700 text-slate-300">
                    <AlignLeft className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignTable('center')}
                    title="Align Center" className="p-1 rounded hover:bg-slate-700 text-slate-300">
                    <AlignCenter className="w-3 h-3" />
                  </button>
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => alignTable('full')}
                    title="Full Width" className="p-1 rounded hover:bg-slate-700 text-slate-300">
                    <Maximize2 className="w-3 h-3" />
                  </button>
                  <div className="w-px h-4 bg-slate-600 mx-0.5" />
                  {/* Delete */}
                  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => {
                    const block = getMoveableBlock(selectedTable) || selectedTable;
                    block.remove();
                    setSelectedTable(null);
                    setSelectedCell(null);
                    handleEditorInput();
                  }}
                    className="px-2 py-1 text-[11px] font-bold rounded-md hover:bg-rose-700 flex items-center gap-1 text-rose-300">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === 'code' && (
            <textarea
              value={htmlCode}
              onChange={handleCodeChange}
              style={{ minHeight }}
              placeholder="Paste or write raw HTML code here..."
              className="w-full h-full font-mono text-xs p-3 bg-slate-950 text-emerald-400 rounded-xl outline-none border border-slate-800 focus:border-brand-500 leading-relaxed"
            />
          )}

          {activeTab === 'preview' && (
            <div
              style={{ minHeight }}
              className="rich-text-content p-4 bg-white rounded-xl border border-slate-200 text-slate-800 text-xs sm:text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlCode) }}
            />
          )}
        </div>

        {/* Footer Status & Sanitization Notice */}
        <div className="px-4 py-2 bg-white border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500 font-medium">
          <span>Mode: <strong className="text-slate-700 dark:text-slate-300 uppercase">{activeTab}</strong></span>
          <span className="flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-emerald-500" /> Safe Semantic HTML output
          </span>
        </div>
      </div>

      {/* INSERT LINK MODAL */}
      <Modal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        title="Insert Hyperlink"
        maxWidth="md"
        zIndexClass="z-[120]"
      >
        <div className="admin-theme flex flex-col gap-4 py-2 text-xs">
          <Input
            label="Link URL (e.g. https://example.com)"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://..."
            required
          />
          <Input
            label="Display Text (optional if text is selected)"
            value={linkText}
            onChange={(e) => setLinkText(e.target.value)}
            placeholder="Click here for warranty info"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsLinkModalOpen(false)}>Cancel</Button>
            <Button type="button" variant="primary" size="sm" onClick={handleInsertLink}>Insert Link</Button>
          </div>
        </div>
      </Modal>

      {/* INSERT IMAGE MODAL */}
      <Modal
        isOpen={isImgModalOpen}
        onClose={resetImageModal}
        title={editingImage ? 'Edit Image' : 'Insert Image'}
        maxWidth="md"
        zIndexClass="z-[120]"
      >
        <div className="admin-theme flex flex-col gap-4 py-2 text-xs">
          {/* Method tabs: URL vs File Upload */}
          <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
            <button
              type="button"
              onClick={() => setImgMode('url')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 ${
                imgMode === 'url'
                  ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-400'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <FileImage className="w-3.5 h-3.5" /> Image URL
            </button>
            <button
              type="button"
              onClick={() => setImgMode('upload')}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors flex items-center gap-1.5 ${
                imgMode === 'upload'
                  ? 'bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-400'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Upload className="w-3.5 h-3.5" /> Upload File
            </button>
          </div>

          {imgMode === 'url' ? (
            <div className="flex flex-col gap-3">
              <Input
                label="Image Web Address (URL)"
                value={imgUrl}
                onChange={(e) => setImgUrl(e.target.value)}
                placeholder="https://images.unsplash.com/photo-..."
                required
              />
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 flex-wrap">
                <span>Quick examples:</span>
                <button
                  type="button"
                  onClick={() => setImgUrl('https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=600&auto=format&fit=crop')}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                >
                  AC Maintenance
                </button>
                <span>•</span>
                <button
                  type="button"
                  onClick={() => setImgUrl('https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=600&auto=format&fit=crop')}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                >
                  Electrical Service
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <label className="font-semibold text-slate-700 dark:text-slate-300 text-xs">
                Select Image File (PNG, JPG, WebP &lt; 3MB)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFileChange}
                className="block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-brand-50 file:text-brand-700 dark:file:bg-brand-950 dark:file:text-brand-300 hover:file:bg-brand-100 cursor-pointer border border-slate-200 dark:border-slate-800 rounded-xl p-2"
              />
            </div>
          )}

          <Input
            label="Alt / Description (for Accessibility)"
            value={imgAlt}
            onChange={(e) => setImgAlt(e.target.value)}
            placeholder="Service step illustration or spare part"
          />

          {/* Alignment Choice */}
          <div className="flex flex-col gap-1.5">
            <label className="font-semibold text-slate-700 dark:text-slate-300 text-xs">Alignment</label>
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => setImgAlign('left')}
                className={`py-1.5 px-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                  imgAlign === 'left'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 font-bold'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <AlignLeft className="w-3.5 h-3.5" /> Left
              </button>
              <button
                type="button"
                onClick={() => setImgAlign('center')}
                className={`py-1.5 px-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                  imgAlign === 'center'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 font-bold'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <AlignCenter className="w-3.5 h-3.5" /> Center
              </button>
              <button
                type="button"
                onClick={() => setImgAlign('right')}
                className={`py-1.5 px-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                  imgAlign === 'right'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 font-bold'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <AlignRight className="w-3.5 h-3.5" /> Right
              </button>
              <button
                type="button"
                onClick={() => setImgAlign('full')}
                className={`py-1.5 px-2 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1 transition-colors ${
                  imgAlign === 'full'
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-950/60 text-brand-600 dark:text-brand-400 font-bold'
                    : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                }`}
              >
                <Maximize2 className="w-3.5 h-3.5" /> Full
              </button>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Input
              label={`Display width (${imgWidth}%)`}
              type="range"
              min={20}
              max={100}
              value={imgWidth}
              onChange={(e) => setImgWidth(e.target.value)}
            />
            <Input
              label="Display height (px; leave blank for automatic)"
              type="number"
              min={20}
              max={1200}
              value={imgHeight === 'auto' ? '' : imgHeight}
              onChange={(e) => setImgHeight(e.target.value || 'auto')}
            />
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400 font-semibold">Presets:</span>
              {(['25', '50', '75', '100']).map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setImgWidth(pct)}
                  className={`px-2 py-0.5 rounded text-[11px] font-bold border transition-colors ${
                    imgWidth === pct
                      ? 'bg-brand-500 text-white border-brand-500'
                      : 'border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                  }`}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          {/* Live Preview inside modal */}
          {imgUrl && (
            <div className="mt-1 p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col gap-2">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500">
                <span>Preview:</span>
                <button
                  type="button"
                  onClick={() => setImgUrl('')}
                  className="text-rose-500 hover:underline flex items-center gap-1"
                >
                  <X className="w-3 h-3" /> Clear
                </button>
              </div>
              <div className="max-h-40 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-center p-1">
                <img
                  src={normalizeSafeUrl(imgUrl) || imgUrl}
                  alt={imgAlt || 'Preview'}
                  referrerPolicy="no-referrer-when-downgrade"
                  className="max-h-36 max-w-full object-contain rounded"
                  style={{
                    width: `${Math.max(20, Math.min(100, Number(imgWidth) || 100))}%`,
                    height: /^\d{1,4}$/.test(imgHeight) ? `${Math.max(20, Math.min(1200, Number(imgHeight)))}px` : 'auto',
                    maxHeight: imgHeight === 'auto' ? undefined : 'none',
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Button type="button" variant="outline" size="sm" onClick={resetImageModal}>Cancel</Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleInsertImage}
              disabled={!imgUrl.trim()}
            >
              {editingImage ? 'Save Image' : 'Insert Image'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* INSERT TABLE MODAL */}
      <Modal
        isOpen={isTableModalOpen}
        onClose={() => setIsTableModalOpen(false)}
        title="Insert Semantic Data Table"
        maxWidth="md"
        zIndexClass="z-[120]"
      >
        <div className="admin-theme flex flex-col gap-4 py-2 text-xs">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Number of Rows"
              type="number"
              value={tableRows}
              onChange={(e) => setTableRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            />
            <Input
              label="Number of Columns"
              type="number"
              value={tableCols}
              onChange={(e) => setTableCols(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={includeTableHeader}
              onChange={(e) => setIncludeTableHeader(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-500 w-4 h-4"
            />
            <span>Include Header Row (&lt;thead&gt;)</span>
          </label>

          {/* Mini visual table preview */}
          <div className="p-3 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col gap-1.5">
            <span className="text-[11px] font-bold text-slate-400">Layout Preview ({tableRows} rows × {tableCols} cols):</span>
            <div className="border border-slate-300 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900 text-[10px]">
              <table className="w-full border-collapse">
                {includeTableHeader && (
                  <thead>
                    <tr className="bg-slate-100 dark:bg-slate-800 font-bold border-b border-slate-300 dark:border-slate-700">
                      {Array.from({ length: Math.min(tableCols, 5) }).map((_, c) => (
                        <th key={c} className="p-1 border-r border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                          Col {c + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {Array.from({ length: Math.min(tableRows, 4) }).map((_, r) => (
                    <tr key={r} className="border-b border-slate-200 dark:border-slate-800 last:border-b-0">
                      {Array.from({ length: Math.min(tableCols, 5) }).map((_, c) => (
                        <td key={c} className="p-1 border-r border-slate-200 dark:border-slate-800 text-slate-400">
                          Cell
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {tableCols > 5 && (
              <span className="text-[10px] text-slate-400 italic">+ {tableCols - 5} more columns...</span>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <Button type="button" variant="outline" size="sm" onClick={() => setIsTableModalOpen(false)}>Cancel</Button>
            <Button type="button" variant="primary" size="sm" onClick={handleInsertTable}>Insert Table</Button>
          </div>
        </div>
      </Modal>

    </div>
  );
};
